import React, { useState, useCallback, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faSpinner, faMicrophone } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { globalLogout } from "../../Utils/Logout";
const SearchBar = ({ isMobile = false, onSearchSubmit }) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [suggestions, setSuggestions] = useState([]);
	const [filteredSuggestions, setFilteredSuggestions] = useState([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [isSearching, setIsSearching] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const [isListening, setIsListening] = useState(false);
	const [isVoiceSupported, setIsVoiceSupported] = useState(false);
	const navigate = useNavigate();
	const searchInputRef = useRef(null);
	const searchContainerRef = useRef(null);
	const recognitionRef = useRef(null);

	// Check if device is mobile
	const isMobileDevice = () => {
		return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
	};

	// Debounce function
	const debounce = (func, delay) => {
		let timer;
		return function (...args) {
			clearTimeout(timer);
			timer = setTimeout(() => func.apply(this, args), delay);
		};
	};

	// Filter products based on published status
	const filterPublishedProducts = useCallback((products) => {
		return products.filter(product => {
			// Check if product is published by seller and admin
			const isProductPublished = product.published && product.adminPublished;

			// Check if brand is published
			const isBrandPublished = product.brand?.published && product.brand?.adminPublished;

			// Check if category is published
			const isCategoryPublished = product.category?.published;

			// Check if seller is active (not suspended)
			const isSellerActive = product.seller?.status === "Active";

			return isProductPublished && isBrandPublished && isCategoryPublished && isSellerActive;
		});
	}, []);

	// Fetch search suggestions
	const fetchSuggestions = useCallback(async (query) => {
		if (!query.trim()) {
			setSuggestions([]);
			setFilteredSuggestions([]);
			setShowSuggestions(false);
			return;
		}

		try {
			setIsSearching(true);
			const response = await axios.get(
				`${process.env.REACT_APP_BACKEND_URL}/api/buyer/searchProducts?query=${query}`
			);
			setSuggestions(response.data.products || []);

			// Filter the products based on published status
			const filtered = filterPublishedProducts(response.data.products || []);
			setFilteredSuggestions(filtered);
		} catch (error) {
			globalLogout(error)
			setSuggestions([]);
			setFilteredSuggestions([]);
		} finally {
			setIsSearching(false);
		}
	}, [filterPublishedProducts]);

	// Debounced search function
	const debouncedSearch = useCallback(
		debounce((query) => fetchSuggestions(query), 300),
		[fetchSuggestions]
	);

	// Request microphone permission
	const requestMicrophonePermission = async () => {
		try {
			if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
				await navigator.mediaDevices.getUserMedia({ audio: true });
				return true;
			}
			return false;
		} catch (error) {
			console.error("Microphone permission error:", error);
			return false;
		}
	};

	// Initialize speech recognition
	useEffect(() => {
		const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
		setIsVoiceSupported(!!SpeechRecognition);

		if (SpeechRecognition) {
			const recognition = new SpeechRecognition();
			recognition.continuous = false;
			recognition.interimResults = false;
			recognition.lang = 'en-US';

			recognition.onresult = (event) => {
				const transcript = event.results[0][0].transcript;
				// Remove trailing full stop if present
				const cleanedTranscript = transcript.replace(/\.$/, '').trim();
				setSearchQuery(cleanedTranscript);
				fetchSuggestions(cleanedTranscript);
				setIsListening(false);
			};

			recognition.onerror = (event) => {
				console.error('Speech recognition error', event.error);
				setIsListening(false);

				// Provide user-friendly error messages
				if (event.error === 'not-allowed') {
					alert("Microphone access is required for voice search. Please allow microphone access in your browser settings.");
				} else if (event.error === 'no-speech') {
					alert("No speech was detected. Please try again.");
				}
			};

			recognition.onend = () => {
				setIsListening(false);
			};

			recognition.onstart = () => {
				setIsListening(true);
			};

			recognitionRef.current = recognition;
		}

		return () => {
			if (recognitionRef.current) {
				recognitionRef.current.stop();
			}
		};
	}, [fetchSuggestions]);

	// Handle mobile-specific speech recognition issues
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.hidden && isListening) {
				// If the page is hidden (e.g., app switcher) and we're listening, stop recognition
				if (recognitionRef.current) {
					recognitionRef.current.stop();
				}
				setIsListening(false);
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [isListening]);

	// Handle search input change
	const handleSearchChange = (e) => {
		const query = e.target.value;
		setSearchQuery(query);
		if (!query.trim()) {
			setSuggestions([]);
			setFilteredSuggestions([]);
			setShowSuggestions(false);
		} else {
			debouncedSearch(query);
			setShowSuggestions(true);
		}
	};

	// Handle suggestion click
	const handleSuggestionClick = (slug) => {
		navigate(`/product/${slug}`);
		setSearchQuery("");
		setSuggestions([]);
		setFilteredSuggestions([]);
		setShowSuggestions(false);
		searchInputRef.current?.blur();
	};

	// Handle search submit
	const handleSearchSubmit = (e) => {
		e.preventDefault();
		// if (searchQuery.trim()) {
		//   if (onSearchSubmit) {
		//     onSearchSubmit(searchQuery);
		//   } else {
		//     navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
		//   }
		//   setSearchQuery("");
		//   setSuggestions([]);
		//   setFilteredSuggestions([]);
		//   setShowSuggestions(false);
		//   searchInputRef.current?.blur();
		// }
	};

	// Toggle voice recognition
	const toggleVoiceRecognition = async () => {
		if (!isVoiceSupported) {
			if (isMobileDevice()) {
				alert("Voice search works best on Chrome for mobile. Please use Chrome browser for this feature.");
			} else {
				alert("Voice search is not supported in your browser. Try Chrome or Edge.");
			}
			return;
		}

		if (isListening) {
			recognitionRef.current.stop();
			setIsListening(false);
		} else {
			try {
				// On mobile, we need to explicitly request permission
				if (isMobileDevice()) {
					const hasPermission = await requestMicrophonePermission();
					if (!hasPermission) {
						alert("Microphone access is required for voice search. Please allow microphone access in your browser settings.");
						return;
					}
				}

				recognitionRef.current.start();
				setShowSuggestions(true);
				searchInputRef.current?.focus();
			} catch (error) {
				console.error("Error starting voice recognition:", error);
				setIsListening(false);

				// Provide user-friendly error messages
				if (error.message.includes('not allowed') || error.message.includes('Permission denied')) {
					alert("Please allow microphone access to use voice search.");
				} else if (error.message.includes('no speech')) {
					alert("No speech was detected. Please try again.");
				} else if (error.message.includes('audio capture')) {
					alert("Audio capture is not available. Please check your microphone settings.");
				} else {
					alert("Voice search is not available at the moment. Please try again later.");
				}
			}
		}
	};

	// Close suggestions when clicking outside
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
				setShowSuggestions(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleSuggestionMouseDown = (e) => {
		e.preventDefault();
	};

	// Helper function to get the first variant's first image or placeholder
	const getProductImage = (product) => {
		if (product.variants?.length > 0 && product.variants[0].images?.length > 0) {
			return product.variants[0].images[0];
		}
		return "/placeholder-product.jpg";
	};

	// Helper function to get the lowest price from all variants
	const getLowestPrice = (product) => {
		if (!product.variants || product.variants.length === 0) return 0;
		return Math.min(...product.variants.map(v => v.price));
	};

	return (
		<div
			className={`${isMobile ? 'px-4 pb-4' : 'w-full md:w-2/4 lg:w-1/3 xl:w-2/5 mb-4 md:mb-0'} relative search-container`}
			ref={searchContainerRef}
		>
			<form onSubmit={handleSearchSubmit}>
				<div className="flex items-center space-x-2 w-full">
					<div className={`flex items-center border ${isFocused ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'} rounded-lg w-full relative transition-all duration-200 ${isMobile ? '' : ''}`}>
						<input
							ref={searchInputRef}
							type="text"
							placeholder="Search for products..."
							className={`w-full p-2 outline-none border-none rounded-lg ${isMobile ? 'text-sm' : ''}`}
							value={searchQuery}
							onChange={handleSearchChange}
							onFocus={() => {
								if (searchQuery.trim()) {
									setShowSuggestions(true);
								}
								setIsFocused(true);
							}}
							onBlur={() => setIsFocused(false)}
						/>
						{isVoiceSupported && (
							<button
								type="button"
								onClick={toggleVoiceRecognition}
								className={`p-2 ${isListening ? 'text-red-500 animate-pulse' : 'text-gray-500 hover:text-blue-600'} transition-colors duration-200`}
								title="Voice search"
								disabled={isListening}
							>
								<FontAwesomeIcon
									icon={faMicrophone}
									size={isMobile ? "sm" : "lg"}
									className={isListening ? "animate-bounce" : ""}
								/>
							</button>
						)}
						<button
							type="submit"
							className="p-2 text-gray-500 hover:text-blue-600 transition-colors duration-200"
							disabled={isSearching}
						>
							{isSearching ? (
								<FontAwesomeIcon icon={faSpinner} spin size={isMobile ? "sm" : "lg"} />
							) : (
								<FontAwesomeIcon icon={faMagnifyingGlass} size={isMobile ? "sm" : "lg"} />
							)}
						</button>
					</div>
				</div>
				{showSuggestions && (
					<div
						className={`absolute ${isMobile ? 'left-4 right-4' : 'left-0 right-0'} z-50 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-96 overflow-y-auto transition-all duration-200`}
						onMouseDown={handleSuggestionMouseDown}
					>
						{isListening ? (
							<div className="p-3 text-center flex items-center justify-center bg-red-50">
								<div className="relative mr-2">
									<div className="w-4 h-4 bg-red-500 rounded-full absolute animate-ping"></div>
									<div className="w-4 h-4 bg-red-500 rounded-full"></div>
								</div>
								<p className="text-sm text-red-600 font-medium">Listening... Speak now</p>
								<button
									className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-1 rounded"
									onClick={() => {
										recognitionRef.current.stop();
										setIsListening(false);
									}}
								>
									Stop
								</button>
							</div>
						) : isSearching ? (
							<div className="p-3 text-center flex items-center justify-center">
								<FontAwesomeIcon icon={faSpinner} spin className="mr-2 text-blue-500" />
								<p className="text-sm text-gray-600">Searching products...</p>
							</div>
						) : filteredSuggestions.length > 0 ? (
							<>
								<div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
									<p className="text-xs font-medium text-gray-500">PRODUCTS ({filteredSuggestions.length})</p>
								</div>
								{filteredSuggestions.map((product) => (
									<div
										key={product._id}
										className="p-3 hover:bg-blue-50 cursor-pointer flex items-center transition-colors duration-150"
										onClick={() => handleSuggestionClick(product.slug)}
									>
										<img
											src={getProductImage(product)}
											alt={product.name}
											className="w-10 h-10 object-cover mr-3 rounded"
											onError={(e) => {
												e.target.src = "/placeholder-product.jpg";
											}}
										/>
										<div className="flex-1 min-w-0">
											<p className="text-sm font-medium truncate">{product.name}</p>
											<p className="text-xs text-gray-600">₹{getLowestPrice(product).toLocaleString('en-IN')}</p>
										</div>
										<div className="ml-2 text-xs text-gray-400 truncate max-w-[100px]">
											{product.category?.name || 'Uncategorized'}
										</div>
									</div>
								))}
							</>
						) : searchQuery && suggestions.length === 0 ? (
							<div className="p-4 text-center">
								<p className="text-sm text-gray-600">No products found for "{searchQuery}"</p>
								<p className="text-xs text-gray-400 mt-1">Try different keywords</p>
							</div>
						) : searchQuery && filteredSuggestions.length === 0 && suggestions.length > 0 ? (
							<div className="p-4 text-center">
								<p className="text-sm text-gray-600">No published products found for "{searchQuery}"</p>
								<p className="text-xs text-gray-400 mt-1">Some products may not be available</p>
							</div>
						) : (
							<div className="p-4 text-center">
								<p className="text-sm text-gray-600">Start typing to search products</p>
								{isVoiceSupported && (
									<button
										onClick={toggleVoiceRecognition}
										className="mt-2 px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-sm flex items-center mx-auto"
									>
										<FontAwesomeIcon icon={faMicrophone} className="mr-2" />
										Or use voice search
									</button>
								)}
							</div>
						)}
					</div>
				)}
			</form>
		</div>
	);
};

export default SearchBar;
